import { Helmet } from 'react-helmet-async';
import { useAuthStore } from '../stores/authStore';
import DashboardLayout from '../components/layout/DashboardLayout';
import { User as UserIcon, Mail, Calendar, Shield, CreditCard } from 'lucide-react';

export default function Profile() {
  const { user } = useAuthStore();

  const profileFields = [
    {
      label: 'Full Name',
      value: user?.full_name || 'Not set',
      icon: UserIcon,
    },
    {
      label: 'Email',
      value: user?.email || 'N/A',
      icon: Mail,
    },
    {
      label: 'Role',
      value: user?.is_admin ? 'Admin' : 'User',
      icon: Shield,
    },
    {
      label: 'Plan',
      value: user?.plan || 'free',
      icon: CreditCard,
    },
    {
      label: 'Member Since',
      value: user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A',
      icon: Calendar,
    },
  ];

  return (
    <>
      <Helmet><meta name="robots" content="noindex,nofollow" /></Helmet>
    <DashboardLayout>
      <div className="max-w-4xl">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Profile</h1>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Profile Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <div className="w-20 h-20 rounded-full bg-brand-100 dark:bg-brand-900/20 flex items-center justify-center mr-6">
                <span className="text-2xl font-bold text-brand-600 dark:text-brand-400">
                  {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {user?.full_name || user?.email?.split('@')[0] || 'User'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Profile Details */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Account Information
            </h3>
            <div className="space-y-4">
              {profileFields.map((field) => {
                const Icon = field.icon;
                return (
                  <div key={field.label} className="flex items-center py-3 border-b border-gray-200 dark:border-gray-700 last:border-0">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center mr-4">
                      <Icon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 dark:text-gray-400">{field.label}</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white capitalize">
                        {field.value}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Account Status */}
          <div className="p-6 bg-gray-50 dark:bg-gray-700/30 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Account Status
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {user?.is_active ? (user.is_verified ? 'Active and verified' : 'Active') : 'Inactive'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  user?.is_active
                    ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
                    : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
                }`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
                {user?.is_verified && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-400">
                    Verified
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
    </>
  );
}
